import React, { useState, useEffect, useCallback, useRef } from 'react'
import { supabase } from '../supabaseClient'
import './Vendas.css'

const PODE_CONFIGURAR = ['administrador', 'administrativo']

const formatBRL = (v) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 }).format(Number(v) || 0)

// Máscara de dinheiro para inputs (ex.: 150000 -> "1.500,00")
const formatMoneyInput = (input) => {
  const digits = String(input || '').replace(/\D/g, '')
  if (!digits) return ''
  const value = Number(digits) / 100
  return value.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

const parseMoney = (str) => {
  const normalized = String(str || '').replace(/\./g, '').replace(',', '.')
  const n = Number(normalized)
  return Number.isFinite(n) ? n : 0
}

const moneyToInput = (v) => Number(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })

const iniciais = (nome) => {
  const partes = String(nome || '').trim().split(/\s+/)
  if (partes.length === 0 || !partes[0]) return '?'
  if (partes.length === 1) return partes[0].slice(0, 2).toUpperCase()
  return (partes[0][0] + partes[partes.length - 1][0]).toUpperCase()
}

const pad2 = (n) => String(n).padStart(2, '0')

const hojeStr = () => {
  const now = new Date()
  return `${now.getFullYear()}-${pad2(now.getMonth() + 1)}-${pad2(now.getDate())}`
}

// Intervalo do mês atual (primeiro e último dia).
const mesAtualRange = () => {
  const now = new Date()
  const inicioDate = new Date(now.getFullYear(), now.getMonth(), 1)
  const fimDate = new Date(now.getFullYear(), now.getMonth() + 1, 0)
  const fmt = (d) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`
  return { inicio: fmt(inicioDate), fim: fmt(fimDate) }
}

// Recebe 'YYYY-MM-DD' e retorna o dia seguinte (para usar como limite exclusivo).
const proximoDiaStr = (dateStr) => {
  const [y, m, d] = String(dateStr).split('-').map(Number)
  const dt = new Date(y, (m || 1) - 1, (d || 1) + 1)
  return `${dt.getFullYear()}-${pad2(dt.getMonth() + 1)}-${pad2(dt.getDate())}`
}

const Vendas = ({ onBack, userRole, userId, userEmpresa }) => {
  const empresaId = userEmpresa?.id || userEmpresa
  const isEspecialista = userRole === 'especialista'
  const [loading, setLoading] = useState(true)
  const [msg, setMsg] = useState({ text: '', type: '' })
  const [filtroData, setFiltroData] = useState(() => mesAtualRange())
  const [especialistas, setEspecialistas] = useState([])
  const [metasPorUsuario, setMetasPorUsuario] = useState({})
  const [zonas, setZonas] = useState({ verde: 100, amarela: 50, vermelha: 0 })
  const [dados, setDados] = useState({}) // { [usuarioId]: { contatos, agendadas, realizadas, faturamento } }

  // Modal de configuração
  const [showConfig, setShowConfig] = useState(false)
  const [zonasForm, setZonasForm] = useState({ verde: '0,00', amarela: '0,00', vermelha: '0,00' })
  const [metasForm, setMetasForm] = useState({})
  const [salvando, setSalvando] = useState(false)
  // Exceção: usuários (não-especialistas) incluídos manualmente no painel
  const [configUsuarios, setConfigUsuarios] = useState([]) // { id, nome, role, exibir_em_vendas }
  const [incluidosSet, setIncluidosSet] = useState(() => new Set())
  const [buscaUsuario, setBuscaUsuario] = useState('')

  const podeConfigurar = PODE_CONFIGURAR.includes(userRole)

  const dashboardRef = useRef(null)
  const [telaCheia, setTelaCheia] = useState(false)

  const toggleTelaCheia = async () => {
    try {
      if (!document.fullscreenElement) {
        if (dashboardRef.current?.requestFullscreen) await dashboardRef.current.requestFullscreen()
      } else if (document.exitFullscreen) {
        await document.exitFullscreen()
      }
    } catch (e) {
      // Fallback: apenas alterna o modo "somente informações"
      setTelaCheia(prev => !prev)
    }
  }

  useEffect(() => {
    const onFsChange = () => setTelaCheia(!!document.fullscreenElement)
    document.addEventListener('fullscreenchange', onFsChange)
    return () => document.removeEventListener('fullscreenchange', onFsChange)
  }, [])

  const showNotification = (text, type) => {
    setMsg({ text, type })
    setTimeout(() => setMsg({ text: '', type: '' }), 4000)
  }

  const carregar = useCallback(async () => {
    try {
      setLoading(true)
      const inicio = filtroData.inicio || hojeStr()
      const fimBase = filtroData.fim || filtroData.inicio || hojeStr()
      // Limite superior exclusivo: dia seguinte ao "fim" para incluir o dia inteiro.
      const fimExclusivo = proximoDiaStr(fimBase)

      // Especialistas ativos + usuários incluídos manualmente (exceção)
      const { data: usuarios, error: errUsuarios } = await supabase
        .from('usuarios_sistema')
        .select('id, nome, role, exibir_em_vendas')
        .eq('status', 'ativo')
        .or('role.eq.especialista,exibir_em_vendas.eq.true')
        .order('nome', { ascending: true })
      if (errUsuarios) throw errUsuarios
      // Especialista enxerga apenas o próprio registro; demais perfis veem todos.
      const lista = isEspecialista
        ? (usuarios || []).filter(u => String(u.id) === String(userId))
        : (usuarios || [])
      setEspecialistas(lista)
      const ids = lista.map(u => u.id)

      // Zonas
      const { data: zonasData } = await supabase
        .from('metas_zonas')
        .select('verde, amarela, vermelha')
        .order('id', { ascending: true })
        .limit(1)
      if (zonasData && zonasData[0]) {
        setZonas({
          verde: Number(zonasData[0].verde),
          amarela: Number(zonasData[0].amarela),
          vermelha: Number(zonasData[0].vermelha)
        })
        setZonasForm({
          verde: moneyToInput(zonasData[0].verde),
          amarela: moneyToInput(zonasData[0].amarela),
          vermelha: moneyToInput(zonasData[0].vermelha)
        })
      }

      // Metas individuais
      const { data: metasData } = await supabase
        .from('especialista_metas')
        .select('id_usuario, meta_contatos, meta_visitas_agendadas, meta_visitas_realizadas')
      const metasMap = {}
      for (const m of metasData || []) metasMap[m.id_usuario] = m
      setMetasPorUsuario(metasMap)

      const agregados = {}
      ids.forEach(id => { agregados[id] = { contatos: 0, agendadas: 0, realizadas: 0, faturamento: 0 } })

      if (ids.length > 0) {
        // Contatos (leads cadastrados hoje pelo especialista)
        const { data: leadsData } = await supabase
          .from('leads')
          .select('responsavel')
          .in('responsavel', ids)
          .gte('created_at', inicio)
          .lt('created_at', fimExclusivo)
        for (const l of leadsData || []) {
          if (agregados[l.responsavel]) agregados[l.responsavel].contatos += 1
        }

        // Visitas (eventos do calendário de hoje, separados por executado)
        const { data: eventosData } = await supabase
          .from('eventos_calendario')
          .select('id_responsavel, executado')
          .in('id_responsavel', ids)
          .gte('data_hora', inicio)
          .lt('data_hora', fimExclusivo)
        for (const ev of eventosData || []) {
          const ag = agregados[ev.id_responsavel]
          if (!ag) continue
          if (ev.executado) ag.realizadas += 1
          else ag.agendadas += 1
        }

        // Faturamento do dia (clientes cadastrados hoje pelo especialista)
        const { data: clientesData } = await supabase
          .from('clientes_cadastro')
          .select('id_responsavel, valor_total')
          .in('id_responsavel', ids)
          .gte('data_cadastro', inicio)
          .lt('data_cadastro', fimExclusivo)
        for (const c of clientesData || []) {
          if (agregados[c.id_responsavel]) agregados[c.id_responsavel].faturamento += Number(c.valor_total) || 0
        }
      }

      setDados(agregados)
    } catch (err) {
      console.error('Erro ao carregar painel de vendas:', err)
      showNotification(err.message || 'Erro ao carregar dados.', 'error')
    } finally {
      setLoading(false)
    }
  }, [filtroData.inicio, filtroData.fim, isEspecialista, userId])

  useEffect(() => {
    carregar()
  }, [carregar])

  const pct = (valor, meta) => {
    const m = Number(meta) || 0
    if (m <= 0) return valor > 0 ? 100 : 0
    return Math.round((Number(valor) / m) * 100)
  }

  // Cor de cada métrica conforme atingimento da própria meta
  const corPorMeta = (valor, meta) => {
    const p = pct(valor, meta)
    if (p >= 100) return 'green'
    if (p >= 50) return 'yellow'
    return 'red'
  }

  // Zona (e cor do faturamento) conforme o faturamento do dia vs faixas em R$
  const corPorFaturamento = (faturamento) => {
    const fat = Number(faturamento) || 0
    if (fat >= zonas.verde) return 'green'
    if (fat >= zonas.amarela) return 'yellow'
    return 'red'
  }

  const abrirConfig = async () => {
    setBuscaUsuario('')
    setShowConfig(true)

    try {
      // Zonas salvas
      const { data: zonasData } = await supabase
        .from('metas_zonas')
        .select('verde, amarela, vermelha')
        .order('id', { ascending: true })
        .limit(1)
      if (zonasData && zonasData[0]) {
        setZonasForm({
          verde: moneyToInput(zonasData[0].verde),
          amarela: moneyToInput(zonasData[0].amarela),
          vermelha: moneyToInput(zonasData[0].vermelha)
        })
      } else {
        setZonasForm({ verde: moneyToInput(zonas.verde), amarela: moneyToInput(zonas.amarela), vermelha: moneyToInput(zonas.vermelha) })
      }

      // Metas individuais salvas
      const { data: metasData } = await supabase
        .from('especialista_metas')
        .select('id_usuario, meta_contatos, meta_visitas_agendadas, meta_visitas_realizadas')
      const metasMap = {}
      for (const m of metasData || []) metasMap[m.id_usuario] = m
      setMetasPorUsuario(metasMap)

      const formMetas = {}
      especialistas.forEach(u => {
        const m = metasMap[u.id]
        formMetas[u.id] = {
          meta_contatos: String(m?.meta_contatos ?? 0),
          meta_visitas_agendadas: String(m?.meta_visitas_agendadas ?? 0),
          meta_visitas_realizadas: String(m?.meta_visitas_realizadas ?? 0)
        }
      })
      setMetasForm(formMetas)

      // Lista de usuários ativos não-especialistas da empresa do usuário logado (candidatos à exceção)
      let usuariosQuery = supabase
        .from('usuarios_sistema')
        .select('id, nome, role, exibir_em_vendas')
        .eq('status', 'ativo')
        .neq('role', 'especialista')
        .order('nome', { ascending: true })
      if (empresaId) {
        usuariosQuery = usuariosQuery.eq('empresa_id', empresaId)
      }
      const { data, error } = await usuariosQuery
      if (error) throw error
      setConfigUsuarios(data || [])
      setIncluidosSet(new Set((data || []).filter(u => u.exibir_em_vendas).map(u => u.id)))
    } catch (err) {
      console.error('Erro ao carregar configurações:', err)
      showNotification('Erro ao carregar configurações salvas.', 'error')
    }
  }

  const toggleIncluido = (id) => {
    setIncluidosSet(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const salvarConfig = async () => {
    try {
      setSalvando(true)

      // Zonas (upsert na única linha) — valores em R$
      const verde = parseMoney(zonasForm.verde)
      const amarela = parseMoney(zonasForm.amarela)
      const vermelha = parseMoney(zonasForm.vermelha)

      const { data: zonaExistente } = await supabase
        .from('metas_zonas')
        .select('id')
        .order('id', { ascending: true })
        .limit(1)
      if (zonaExistente && zonaExistente[0]) {
        const { error } = await supabase.from('metas_zonas').update({ verde, amarela, vermelha }).eq('id', zonaExistente[0].id)
        if (error) throw error
      } else {
        const { error } = await supabase.from('metas_zonas').insert({ verde, amarela, vermelha })
        if (error) throw error
      }

      // Metas individuais (upsert por id_usuario)
      const rows = especialistas.map(u => ({
        id_usuario: u.id,
        meta_contatos: parseInt(metasForm[u.id]?.meta_contatos, 10) || 0,
        meta_visitas_agendadas: parseInt(metasForm[u.id]?.meta_visitas_agendadas, 10) || 0,
        meta_visitas_realizadas: parseInt(metasForm[u.id]?.meta_visitas_realizadas, 10) || 0
      }))
      if (rows.length > 0) {
        const { error } = await supabase.from('especialista_metas').upsert(rows, { onConflict: 'id_usuario' })
        if (error) throw error
      }

      // Exceções: atualizar exibir_em_vendas somente para usuários que mudaram
      const paraIncluir = []
      const paraExcluir = []
      configUsuarios.forEach(u => {
        const desejado = incluidosSet.has(u.id)
        if (desejado && !u.exibir_em_vendas) paraIncluir.push(u.id)
        if (!desejado && u.exibir_em_vendas) paraExcluir.push(u.id)
      })
      if (paraIncluir.length > 0) {
        const { error } = await supabase.from('usuarios_sistema').update({ exibir_em_vendas: true }).in('id', paraIncluir)
        if (error) throw error
      }
      if (paraExcluir.length > 0) {
        const { error } = await supabase.from('usuarios_sistema').update({ exibir_em_vendas: false }).in('id', paraExcluir)
        if (error) throw error
      }

      showNotification('Metas salvas com sucesso!', 'success')
      setShowConfig(false)
      carregar()
    } catch (err) {
      console.error('Erro ao salvar metas:', err)
      showNotification('Erro ao salvar metas: ' + (err.message || 'erro desconhecido'), 'error')
    } finally {
      setSalvando(false)
    }
  }

  const badgeInfo = (cor) => {
    if (cor === 'green') return { cls: 'vendas-badge-verde', label: '✔ VERDE' }
    if (cor === 'yellow') return { cls: 'vendas-badge-amarela', label: '! AMARELA' }
    return { cls: 'vendas-badge-vermelha', label: '✘ VERMELHA' }
  }

  return (
    <div className="vendas-page">
      <div className={`vendas-dashboard${telaCheia ? ' vendas-dashboard-fullscreen' : ''}`} ref={dashboardRef}>
        {telaCheia && (
          <button type="button" className="vendas-btn-sair-fullscreen" onClick={toggleTelaCheia} title="Sair da tela cheia (Esc)">⤢ Sair</button>
        )}
        <div className="vendas-header">
          <div className="vendas-logo">
            <h1>HANGSIX<br /><small>ENERGIA SOLAR</small></h1>
          </div>
          <div className="vendas-title">
            <h2>PAINEL DE ATIVIDADE <span>DIÁRIA</span></h2>
            <p>INTENSIDADE TODOS OS DIAS. LIBERDADE PARA TODOS.</p>
          </div>
          <div className="vendas-slogan">
            NÃO VENDEMOS PLACA SOLAR, VENDEMOS ECONOMIA.<br />
            <strong>ENERGIA SOLAR NÃO É UM PRODUTO, É A LIBERDADE QUE VOCÊ PRECISA.</strong>
          </div>
        </div>

        <div className="vendas-toolbar" style={telaCheia ? { display: 'none' } : undefined}>
          {onBack && (
            <button type="button" className="vendas-btn-voltar" onClick={onBack}>← Voltar</button>
          )}
          <div className="vendas-filtro-datas">
            <label>
              <span>Início</span>
              <input
                type="date"
                value={filtroData.inicio}
                max={filtroData.fim || undefined}
                onChange={(e) => setFiltroData(prev => ({ ...prev, inicio: e.target.value }))}
              />
            </label>
            <label>
              <span>Fim</span>
              <input
                type="date"
                value={filtroData.fim}
                min={filtroData.inicio || undefined}
                onChange={(e) => setFiltroData(prev => ({ ...prev, fim: e.target.value }))}
              />
            </label>
          </div>
          <div className="vendas-toolbar-actions">
            <button type="button" className="vendas-btn-secundario" onClick={carregar} disabled={loading}>
              {loading ? 'Atualizando…' : '↻ Atualizar'}
            </button>
            <button type="button" className="vendas-btn-secundario" onClick={toggleTelaCheia}>⛶ Tela cheia</button>
            {podeConfigurar && (
              <button type="button" className="vendas-btn-config" onClick={abrirConfig}>⚙️ Configurar Metas</button>
            )}
          </div>
        </div>

        {msg.text && <div className={`vendas-msg vendas-msg-${msg.type}`}>{msg.text}</div>}

        <div className="vendas-tabela-wrap">
          <table className="vendas-tabela">
            <thead>
              <tr>
                <th>Vendedor</th>
                <th>👤 Contatos</th>
                <th>🗓️ Visitas Agendadas</th>
                <th>🏠 Visitas Realizadas</th>
                <th>💰 Faturamento do Dia</th>
                <th>Zona</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={6} className="vendas-empty">Carregando…</td></tr>
              ) : especialistas.length === 0 ? (
                <tr><td colSpan={6} className="vendas-empty">Nenhum especialista ativo encontrado.</td></tr>
              ) : (
                especialistas.map(u => {
                  const linha = dados[u.id] || { contatos: 0, agendadas: 0, realizadas: 0, faturamento: 0 }
                  const metas = metasPorUsuario[u.id]
                  const pContatos = pct(linha.contatos, metas?.meta_contatos)
                  const pAgendadas = pct(linha.agendadas, metas?.meta_visitas_agendadas)
                  const pRealizadas = pct(linha.realizadas, metas?.meta_visitas_realizadas)
                  const zonaCor = corPorFaturamento(linha.faturamento)
                  const badge = badgeInfo(zonaCor)
                  return (
                    <tr key={u.id}>
                      <td>
                        <div className="vendas-vendedor">
                          <div className="vendas-avatar">{iniciais(u.nome)}</div>
                          <span>{(u.nome || '').toUpperCase()}</span>
                        </div>
                      </td>
                      <td>
                        <span className={`vendas-val vendas-${corPorMeta(linha.contatos, metas?.meta_contatos)}`}>{linha.contatos}</span>
                        <span className="vendas-pct">{pContatos}%{metas ? ` de ${metas.meta_contatos}` : ''}</span>
                      </td>
                      <td>
                        <span className={`vendas-val vendas-${corPorMeta(linha.agendadas, metas?.meta_visitas_agendadas)}`}>{linha.agendadas}</span>
                        <span className="vendas-pct">{pAgendadas}%{metas ? ` de ${metas.meta_visitas_agendadas}` : ''}</span>
                      </td>
                      <td>
                        <span className={`vendas-val vendas-${corPorMeta(linha.realizadas, metas?.meta_visitas_realizadas)}`}>{linha.realizadas}</span>
                        <span className="vendas-pct">{pRealizadas}%{metas ? ` de ${metas.meta_visitas_realizadas}` : ''}</span>
                      </td>
                      <td className={`vendas-faturamento vendas-${zonaCor}`}>{formatBRL(linha.faturamento)}</td>
                      <td>
                        <div className={`vendas-badge ${badge.cls}`}>{badge.label}</div>
                      </td>
                    </tr>
                  )
                })
              )}
            </tbody>
          </table>
        </div>

        <div className="vendas-footer">
          <div>⚡ Intensidade gera resultados</div>
          <div>🎯 Foco no processo, resultado é consequência</div>
          <div>👥 Disciplina todos os dias</div>
        </div>
      </div>

      {showConfig && (
        <div className="vendas-modal-overlay">
          <div className="vendas-modal" onClick={(e) => e.stopPropagation()}>
            <div className="vendas-modal-header">
              <h3>⚙️ Configurar Metas e Zonas</h3>
              <button type="button" className="vendas-modal-close" onClick={() => !salvando && setShowConfig(false)}>×</button>
            </div>
            <div className="vendas-modal-body">
              <h4>Zonas de desempenho (faturamento mínimo do dia)</h4>
              <div className="vendas-zonas-grid">
                <div className="vendas-field">
                  <label>Verde (R$)</label>
                  <input type="text" inputMode="numeric" placeholder="0,00" value={zonasForm.verde} onChange={(e) => setZonasForm(p => ({ ...p, verde: formatMoneyInput(e.target.value) }))} />
                </div>
                <div className="vendas-field">
                  <label>Amarela (R$)</label>
                  <input type="text" inputMode="numeric" placeholder="0,00" value={zonasForm.amarela} onChange={(e) => setZonasForm(p => ({ ...p, amarela: formatMoneyInput(e.target.value) }))} />
                </div>
                <div className="vendas-field">
                  <label>Vermelha (R$)</label>
                  <input type="text" inputMode="numeric" placeholder="0,00" value={zonasForm.vermelha} onChange={(e) => setZonasForm(p => ({ ...p, vermelha: formatMoneyInput(e.target.value) }))} />
                </div>
              </div>

              <h4 style={{ marginTop: 20 }}>Metas por especialista</h4>
              {especialistas.length === 0 ? (
                <p className="vendas-empty">Nenhum especialista ativo.</p>
              ) : (
                <div className="vendas-metas-table-wrap">
                  <table className="vendas-metas-table">
                    <thead>
                      <tr>
                        <th>Especialista</th>
                        <th>Contatos</th>
                        <th>Visitas Agendadas</th>
                        <th>Visitas Realizadas</th>
                      </tr>
                    </thead>
                    <tbody>
                      {especialistas.map(u => (
                        <tr key={u.id}>
                          <td className="vendas-metas-nome">{u.nome}</td>
                          <td>
                            <input
                              type="number"
                              value={metasForm[u.id]?.meta_contatos ?? '0'}
                              onChange={(e) => setMetasForm(prev => ({ ...prev, [u.id]: { ...prev[u.id], meta_contatos: e.target.value } }))}
                            />
                          </td>
                          <td>
                            <input
                              type="number"
                              value={metasForm[u.id]?.meta_visitas_agendadas ?? '0'}
                              onChange={(e) => setMetasForm(prev => ({ ...prev, [u.id]: { ...prev[u.id], meta_visitas_agendadas: e.target.value } }))}
                            />
                          </td>
                          <td>
                            <input
                              type="number"
                              value={metasForm[u.id]?.meta_visitas_realizadas ?? '0'}
                              onChange={(e) => setMetasForm(prev => ({ ...prev, [u.id]: { ...prev[u.id], meta_visitas_realizadas: e.target.value } }))}
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              <h4 style={{ marginTop: 20 }}>Incluir usuários no painel (exceção)</h4>
              <p className="vendas-config-hint">Marque usuários não-especialistas que também devem aparecer nos resultados.</p>
              <input
                type="text"
                className="vendas-busca-usuario"
                placeholder="Buscar usuário..."
                value={buscaUsuario}
                onChange={(e) => setBuscaUsuario(e.target.value)}
              />
              <div className="vendas-usuarios-lista">
                {configUsuarios.length === 0 ? (
                  <p className="vendas-empty">Nenhum usuário disponível.</p>
                ) : (
                  configUsuarios
                    .filter(u => (u.nome || '').toLowerCase().includes(buscaUsuario.trim().toLowerCase()))
                    .map(u => (
                      <label key={u.id} className="vendas-usuario-item">
                        <input
                          type="checkbox"
                          checked={incluidosSet.has(u.id)}
                          onChange={() => toggleIncluido(u.id)}
                        />
                        <span>{u.nome} <small>({u.role})</small></span>
                      </label>
                    ))
                )}
              </div>
            </div>
            <div className="vendas-modal-footer">
              <button type="button" className="vendas-btn-secundario" onClick={() => setShowConfig(false)} disabled={salvando}>Cancelar</button>
              <button type="button" className="vendas-btn-config" onClick={salvarConfig} disabled={salvando}>
                {salvando ? 'Salvando…' : 'Salvar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default Vendas
